import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

const STORAGE_KEY = "appTheme";

const getSystemTheme = () =>
  window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";

const resolveTheme = (preference) => {
  if (preference === "system") return getSystemTheme();
  return preference === "dark" ? "dark" : "light";
};

const applyThemeClass = (resolved) => {
  document.body.classList.toggle("dark-theme", resolved === "dark");
  document.documentElement.setAttribute("data-theme", resolved);
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
    return "light";
  });

  const resolvedTheme = useMemo(() => resolveTheme(theme), [theme]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, theme);
    applyThemeClass(resolvedTheme);
  }, [theme, resolvedTheme]);

  useEffect(() => {
    if (theme !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyThemeClass(getSystemTheme());
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      if (prev === "light") return "dark";
      if (prev === "dark") return "system";
      return "light";
    });
  }, []);

  const setThemePreference = useCallback((next) => {
    if (next === "light" || next === "dark" || next === "system") {
      setTheme(next);
    }
  }, []);

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      isDark: resolvedTheme === "dark",
      toggleTheme,
      setTheme: setThemePreference,
    }),
    [theme, resolvedTheme, toggleTheme, setThemePreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
